import cx from 'classnames'
import _ from '@/utils/lodash-optimized'
import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import { ClientNameContext } from '../../../lib/context/client-name'
import { PatientIdContext } from '../../../lib/context/patient-id'
import * as Encounter from '../../../selectors/encounter'
import * as Insurance from '../../../selectors/insurance'
import * as MedicalTeam from '../../../selectors/medicalTeam'
import * as Patient from '../../../selectors/patient'
import * as Symptom from '../../../selectors/symptom'
import { mapFuncAttrsToObject } from '../../../selectors/utils'
import { ParentMeasureProvider } from '../../shared/measure'
import { CircularProgress } from '../../shared/NarusMaterial'

import './styles'

import Section1 from './components/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'
import Section4 from './components/Section4'
import Section5 from './components/Section5'

const getClientName = redux => {
    let clients = redux?.globalVars?.clients ?? []
    let clientName = redux?.patient?.clientName
  return (_.find(clients, c => c.clientName === clientName) || {}).display
}
const getSpecPercentage = redux => {
    return redux?.patient?.specPercentage
}

const section1Selector = createSelector(
  [
    Patient.getPatientName,
    Patient.getPatientAge,
    Patient.getPatientDOB,
    Patient.getPatientGender,
    Patient.getPatientSSNLast4,
    Patient.getPatientPAR,
    Patient.getPatientState,
    Patient.isUrgent,
    Patient.getLastStatusUpdateDate,
    Patient.getEnrollmentStatus,
    Patient.isVIPPatient
  ],
  mapFuncAttrsToObject(
    'name',
    'age',
    'dob',
    'gender',
    'ssn',
    'par',
    'patientState',
    'isUrgent',
    'lastStatusUpdateDate',
    'status',
    'isVIPPatient'
  )
)

const section2Selector = createSelector(
  [
    Patient.getPatientHealthProgram,
    Patient.getPatientPrimaryConcern,
    Patient.getPatientResidenceType,
    Patient.getClientDivision,
    getClientName
  ],
  mapFuncAttrsToObject(
    'program',
    'primaryConcern',
    'residenceType',
    'clientDivision',
    'clientName'
  )
)

const section3Selector = createSelector(
  [
    Insurance.getPrimaryInsurancePlusPlan,
    MedicalTeam.getPCPFullNameAndCredentials,
    Patient.getPartnerOrganization,
    Encounter.getLastEncounter,
    Encounter.getLastEncounterType,
    Encounter.getLastEncounterTimeAgo
  ],
  mapFuncAttrsToObject(
    'primaryInsuranceProvider',
    'pcpFullname',
    'partnerOrganization',
    'lastEncounter',
    'lastEncounterType',
    'lastEncounterTimeAgo'
  )
)

const section4Selector = createSelector(
  [
    Symptom.getSymptomsLastUpdateDatetime,
    Symptom.getSymptomsAndColors,
    Symptom.getSymptomsAndColorsGray,
    Symptom.getLastAlertFromObservations
  ],
  mapFuncAttrsToObject(
    'lastUpdateDatetime',
    'symptoms',
    'symptomsGray',
    'doAlert'
  )
)

const section5Selector = createSelector(
  [
    Patient.isHospitalized,
    Patient.isMobileEnrolled,
      Patient.isMemberPortalMobileEnrolled,
    getSpecPercentage,
    Patient.hasAllergies,
    Patient.hasDNR,
    Patient.hasAdvancedDirective,
    Patient.hasPOLST
  ],
  mapFuncAttrsToObject(
    'hospitalized',
    'isMobileEnrolled',
      'isMemberPortalMobileEnrolled',
    'specPercentage',
    'hasAllergies',
    'hasDNR',
    'hasAdvancedDirective',
    'hasPOLST'
  )
)

function Wrapper ({ children, sticky, doNotCall }) {
  return (
    <div
      className={cx('patient-banner', {
        sticky
      })}
    >
      <ParentMeasureProvider>
        <div
          className={cx(`
            flex
            flex-row
            gap-6
            items-center
            justify-between
            w-full
          `, doNotCall ? 'bg-[#fecaca]' : 'bg-white')}
        >
          {children}
        </div>
      </ParentMeasureProvider>
    </div>
  )
}

const PatientBanner = ({
  sticky
}) => {
  let isLoading = useSelector(state => state?.patient?.loading === true)

  let section1 = useSelector((state) => section1Selector(state))
  let section2 = useSelector((state) => section2Selector(state))
  let section3 = useSelector((state) => section3Selector(state))
  let section4 = useSelector((state) => section4Selector(state))
  let section5 = useSelector((state) => section5Selector(state))
  let doNotCall = useSelector((state) => Patient.getPatientDoNotCall(state))

  let patientId = useContext(PatientIdContext)
  let clientName = useContext(ClientNameContext)

  if (isLoading) {
    return (
      <Wrapper
        sticky={sticky}
        doNotCall={doNotCall}
      >
        <div className='loading'>
          <CircularProgress />
        </div>
      </Wrapper>
    )
  }

  return (
    <Wrapper
      sticky={sticky}
      doNotCall={doNotCall}
    >
      <Section1
        {...section1}
        key='1'
      />
      <Section2
        clientKey={clientName}
        {...section2}
        key='2'
      />
      <Section3
        {...section3}
        key='3'
      />
      <Section4
        {...section4}
        key='4'
      />
      <Section5
        clientName={clientName}
        patientId={patientId}
        {...section5}
        doNotCall={doNotCall}
        key='5'
      />
    </Wrapper>

  )
}

export default PatientBanner
